import { Temporal } from 'temporal-polyfill';
import { expandSchedule } from './schedule';
import type { ClassSeries, IsoDate, Location, Occurrence, OccurrenceOverride } from './types';

/**
 * Teaching-hours statistics, computed — like occurrences — never stored.
 *
 * Everything here is derived from the same series/override expansion the
 * schedule uses, so the stats can never disagree with what the calendar shows.
 * `now` is always passed in: this module must stay clock-free so that every
 * counting rule is testable at a fixed instant.
 */

/** Hours taught (or expected) for one class within one window. */
export interface SeriesTotals {
  readonly seriesId: string;
  readonly minutes: number;
  readonly sessions: number;
}

/** One stat window's headline numbers plus its per-class breakdown. */
export interface StatWindowTotals {
  readonly minutes: number;
  readonly sessions: number;
  /** Sorted by minutes descending so the breakdown lists the biggest first. */
  readonly bySeries: readonly SeriesTotals[];
}

export interface StatsRequest {
  readonly series: readonly ClassSeries[];
  readonly locations: readonly Location[];
  readonly overrides?: readonly OccurrenceOverride[];
  /** The instant stats are evaluated at, in the device's timezone. */
  readonly now: Temporal.ZonedDateTime;
}

/**
 * Three kinds of window, three counting rules:
 *
 * - Past windows count only what has already happened (`end ≤ now`), so a class
 *   later today never inflates "past 7 days".
 * - Current calendar windows (this week/month) count every scheduled session in
 *   the period, taught or not — "how many hours am I teaching this week" is a
 *   question about the whole week.
 * - Forward windows starting today count only what has not happened yet, so
 *   this morning's class is not double-billed as still to come.
 */
export interface Stats {
  /** Taught: everything from the earliest series start through now. */
  readonly total: StatWindowTotals;
  /** Taught: the last 7 days through now. */
  readonly past7Days: StatWindowTotals;
  /** Taught: the last 14 days through now. */
  readonly past2Weeks: StatWindowTotals;
  /** Taught: the last calendar month through now. */
  readonly pastMonth: StatWindowTotals;
  /** All sessions in the current ISO week, Monday–Sunday. */
  readonly thisWeek: StatWindowTotals;
  /** All sessions in the current calendar month. */
  readonly thisMonth: StatWindowTotals;
  /** All sessions in the next calendar month. */
  readonly nextMonth: StatWindowTotals;
  /** All sessions in the next three calendar months. */
  readonly next3Months: StatWindowTotals;
  /** Still to come: today through 31 December of the current year. */
  readonly untilYearEnd: StatWindowTotals;
}

export function computeStats(request: StatsRequest): Stats {
  const today = request.now.toPlainDate();
  const nowInstant = request.now.toInstant();

  const weekStart = today.subtract({ days: today.dayOfWeek - 1 });
  const monthEnd = today.with({ day: today.daysInMonth });
  const nextMonthStart = today.with({ day: 1 }).add({ months: 1 });
  const nextMonthEnd = nextMonthStart.with({ day: nextMonthStart.daysInMonth });
  const thirdMonth = nextMonthStart.add({ months: 2 });
  const next3End = thirdMonth.with({ day: thirdMonth.daysInMonth });
  const yearEnd = today.with({ month: 12, day: 31 });

  // One expansion covers every window: from the oldest possible taught date to
  // the furthest future one. Windows are then pure filters over the result,
  // which is what guarantees an occurrence can never be double- or un-counted.
  const from = earliestCountableStart(request.series, today);
  const to = Temporal.PlainDate.compare(next3End, yearEnd) > 0 ? next3End : yearEnd;

  const occurrences = expandCountable(request, from, to);
  const scheduled = occurrences.filter((occurrence) => occurrence.status === 'scheduled');
  const taught = scheduled.filter(
    (occurrence) => Temporal.Instant.compare(occurrence.end.toInstant(), nowInstant) <= 0,
  );
  const expected = scheduled.filter(
    (occurrence) => Temporal.Instant.compare(occurrence.end.toInstant(), nowInstant) > 0,
  );

  const todayIso = today.toString();

  return {
    total: windowTotals(taught, from.toString(), todayIso),
    past7Days: windowTotals(taught, today.subtract({ days: 7 }).toString(), todayIso),
    past2Weeks: windowTotals(taught, today.subtract({ days: 14 }).toString(), todayIso),
    pastMonth: windowTotals(taught, today.subtract({ months: 1 }).toString(), todayIso),
    thisWeek: windowTotals(scheduled, weekStart.toString(), weekStart.add({ days: 6 }).toString()),
    thisMonth: windowTotals(scheduled, today.with({ day: 1 }).toString(), monthEnd.toString()),
    nextMonth: windowTotals(scheduled, nextMonthStart.toString(), nextMonthEnd.toString()),
    next3Months: windowTotals(scheduled, nextMonthStart.toString(), next3End.toString()),
    untilYearEnd: windowTotals(expected, todayIso, yearEnd.toString()),
  };
}

/**
 * Expands the schedule with stats-specific input handling that the schedule
 * view deliberately does not do:
 *
 * - Archived series still count — their history was taught — but only up to the
 *   moment of archival (`archivedAt`, falling back to `updatedAt` for rows
 *   archived before the stamp existed). They are expanded as active copies with
 *   the end date clamped, so `expandSchedule` itself needs no new flags.
 * - Series at removed places still count: the place is soft-deleted, so its
 *   timezone survives on the tombstone and is used as-is.
 */
function expandCountable(
  request: StatsRequest,
  rangeStart: Temporal.PlainDate,
  rangeEnd: Temporal.PlainDate,
): Occurrence[] {
  const timeZoneId = request.now.timeZoneId;

  const series = request.series.map((item) => {
    if (item.active || item.deletedAt !== null) {
      return item;
    }
    const cutoffInstant = item.archivedAt ?? item.updatedAt;
    const cutoffDate = Temporal.Instant.from(cutoffInstant)
      .toZonedDateTimeISO(timeZoneId)
      .toPlainDate()
      .toString();
    const endDate = item.endDate !== null && item.endDate < cutoffDate ? item.endDate : cutoffDate;
    return { ...item, active: true, endDate };
  });

  const locations = request.locations.map((location) =>
    location.deletedAt === null ? location : { ...location, deletedAt: null },
  );

  return expandSchedule({
    series,
    locations,
    overrides: request.overrides ?? [],
    rangeStart,
    rangeEnd,
  }).occurrences;
}

/** The oldest date any countable series could have produced an occurrence on. */
function earliestCountableStart(
  series: readonly ClassSeries[],
  today: Temporal.PlainDate,
): Temporal.PlainDate {
  let earliest = today;
  for (const item of series) {
    if (item.deletedAt !== null) {
      continue;
    }
    const start = Temporal.PlainDate.from(item.startDate);
    if (Temporal.PlainDate.compare(start, earliest) < 0) {
      earliest = start;
    }
  }
  return earliest;
}

/**
 * Sums the occurrences whose *effective* date falls in the inclusive window.
 * Effective (post-move) dates are what land a rescheduled class in the window
 * it actually happens in. ISO dates compare correctly as strings.
 */
function windowTotals(
  occurrences: readonly Occurrence[],
  from: IsoDate,
  to: IsoDate,
): StatWindowTotals {
  const bySeries = new Map<string, { minutes: number; sessions: number }>();
  let minutes = 0;
  let sessions = 0;

  for (const occurrence of occurrences) {
    if (occurrence.date < from || occurrence.date > to) {
      continue;
    }
    minutes += occurrence.durationMinutes;
    sessions += 1;
    const row = bySeries.get(occurrence.seriesId) ?? { minutes: 0, sessions: 0 };
    row.minutes += occurrence.durationMinutes;
    row.sessions += 1;
    bySeries.set(occurrence.seriesId, row);
  }

  const rows: SeriesTotals[] = [...bySeries.entries()]
    .map(([seriesId, row]) => ({ seriesId, minutes: row.minutes, sessions: row.sessions }))
    .sort((a, b) => b.minutes - a.minutes || a.seriesId.localeCompare(b.seriesId));

  return { minutes, sessions, bySeries: rows };
}
